import React, { useState, useEffect, Fragment } from 'react'
import {
  Grid, Dialog, DialogTitle, DialogContent, DialogActions,  Button, TextField, FormControl, InputLabel, Select, MenuItem, Chip, Box, IconButton, List, ListItem, Menu, Tooltip, Typography, Card, Collapse, CardContent, Autocomplete } from '@mui/material'
import BlockUi from 'react-block-ui'
import { PacmanLoader } from 'react-spinners'
import { useDropzone } from 'react-dropzone'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone'
import CheckIcon from '@mui/icons-material/Check'
import CameraAltTwoToneIcon from '@mui/icons-material/CameraAltTwoTone'
import EditIcon from '@mui/icons-material/Edit'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import ImageCropper from '@/housefolios-components/Dialogs/ImageCropper'
// Centralized lodash imports - much cleaner!
import {
  compact,
  get,
  isEmpty,
  map,
  filter,
  cloneDeep,
  set} from '@/utils/lodash'
import { IMAGE_UPLOAD } from '@/housefolios-components/TitleButtons/AddPropertyButton/mutations'
import { useMutation } from '@apollo/client/react'
import clsx from 'clsx'
import { STATES } from '@/constants/states'
import { Opacity } from '@mui/icons-material'

const CONTACT_TYPES = [
  'Agent',
  'Appraiser',
  'Buyer',
  'Contractor',
  'Gap Partner',
  'Hard Money Lender',
  'Home Inspector',
  'Insurance Agent',
  'Investor',
  'Lender',
  'Other',
  'Property Manager',
  'Seller',
  'Title Company',
]

const AddContactDialog = ({
  open,
  onClose,
  onSave,
  contact,
  defaultValues }) => {
  const [loading, setLoading] = useState(false)
  const [showConfirmClose, setShowConfirmClose] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    types: [],
    email: '',
    phone: '',
    company: '',
    notes: '',
    ranking: null,
    address: '',
    city: '',
    state: '',
    area: '',
    optionalFields: {}})

  // Validation state
  const [errors, setErrors] = useState({
    name: false,
    types: false,
    email: false })

  // Image state management
  const [files, setFiles] = useState([contact?.image || null])
  const [changedFile, setChangedFile] = useState(false)
  const [openEditImage, setOpenEditImage] = useState(false)
  const [editImageMoreVert, setEditImageMoreVert] = useState(null)

  const [openAccordion, setOpenAccordion] = useState('')

  // Store initial form data to detect changes
  const [initialFormData, setInitialFormData] = useState(null)
  const [initialFiles, setInitialFiles] = useState([])

  const toggleAccordion = (name) => {
    if (openAccordion === name) setOpenAccordion('')
    else setOpenAccordion(name)
  }

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    if (changedFile) return true

    if (!initialFormData) return false

    // Compare current form data with initial data
    const currentDataString = JSON.stringify(formData)
    const initialDataString = JSON.stringify(initialFormData)

    return currentDataString !== initialDataString
  }

  // Handle close attempt
  const handleCloseAttempt = () => {
    if (hasUnsavedChanges()) {
      setShowConfirmClose(true)
    } else {
      onClose()
    }
  }

  // Handle confirmed close
  const handleConfirmedClose = () => {
    setShowConfirmClose(false)
    handleClose()
  }

  // Handle cancel close
  const handleCancelClose = () => {
    setShowConfirmClose(false)
  }

  // Image handlers
  const handleEditImage = () => {
    const imageSrc = get(files, '0.url') || get(files, '0.preview')
    if (imageSrc) {
      setOpenEditImage(true)
    }
  }

  const handleCloseEditImage = () => {
    setOpenEditImage(false)
  }

  const handleEditImageMoreVertOpen = (event) => {
    setEditImageMoreVert(event.currentTarget)
  }

  const handleEditImageMoreVertClose = () => {
    setEditImageMoreVert(null)
  }

  const saveCroppedImage = async (croppedImage) => {
    setFiles([croppedImage])
    setChangedFile(true)
    handleCloseEditImage()
  }

  const handleClose = () => {
    setFormData({
      name: '',
      types: [],
      email: '',
      phone: '',
      company: '',
      notes: '',
      ranking: null,
      address: '',
      city: '',
      state: '',
      area: '',
      optionalFields: {}})
    setFiles([])
    setChangedFile(false)
    setInitialFormData(null)
    setInitialFiles([])
    setErrors({
      name: false,
      types: false,
      email: false })
    onClose()
  }

  // Dropzone configuration
  const {
    isDragActive,
    isDragAccept,
    isDragReject,
    open: openDropzone,
    getRootProps,
    getInputProps} = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: false,
    accept: {
      'image/*': []},
    onDrop: (acceptedFiles) => {
      map(acceptedFiles, (file) => {
        const reader = new FileReader()
        reader.onload = async () => {
          const newImage = reader.result
          // setFormData((prev) => ({
          //   ...prev,
          //   image: String(newImage),
          // }))
          setFiles([String(newImage)])
          setChangedFile(true)
        }
        reader.readAsDataURL(file)
      })
    }})

  // Populate form when editing an existing contact
  useEffect(() => {
    if (contact) {
      const newFormData = {
        name: contact.name || '',
        types: contact.types || [],
        email: contact.email || '',
        phone: contact.phone || '',
        company: contact.company || '',
        notes: contact.notes || '',
        ranking: contact.ranking || null,
        address: contact.address || '',
        city: contact.city || '',
        state: contact.state || '',
        area: contact.area || '',
        optionalFields: contact.optionalFields || {}}
      setFormData(newFormData)
      setInitialFormData(newFormData)

      const newFiles = contact.image ? [contact.image] : []
      setFiles(newFiles)
      setInitialFiles(newFiles)

      // Reset validation errors
      setErrors({
        name: false,
        types: false,
        email: false })
    } else {
      // Reset form when adding new contact with defaults
      const newFormData = {
        name: '',
        types: defaultValues?.types || [],
        email: '',
        phone: '',
        company: '',
        notes: '',
        ranking: defaultValues?.ranking || null,
        address: '',
        city: '',
        state: defaultValues?.state || '',
        area: '',
        optionalFields: {}}
      setFormData(newFormData)
      setInitialFormData(newFormData)

      setFiles([])
      setInitialFiles([])
      setChangedFile(false)
      setOpenAccordion('')

      // Reset validation errors
      setErrors({
        name: false,
        types: false,
        email: false })
    }
  }, [contact, defaultValues])

  // Generate image thumbnails
  const thumbs = map(compact(files), (file) => (
    <Box key={file.name || 'contact-image'} data-hf="avatar-image" sx={{
      borderRadius: "0.65rem !important",
      "& .blur-load": { backgroundSize: "contain", backgroundPosition: "center", backgroundRepeat: "no-repeat" },
      overflow: "hidden !important",
      outline: "none !important",
      height: "140px !important",
      width: "140px !important",
      lineHeight: "140px !important",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      p: 0,
      backgroundColor: "rgb(96.1617647059%, 96.1617647059%, 97.0147058824%) !important",
      textAlign: "center",
      fontWeight: 700,
      color: "#1bc943",
    }}>
      <Box style={{ backgroundImage: 'url(' + (file.url_small || file.preview || file) + ')'}} sx={{ backgroundSize: "contain", backgroundPosition: "center", backgroundRepeat: "no-repeat" }}>
        <Box component="img" src={file.url || file.preview || file} alt="Contact" loading="lazy" sx={{
          maxWidth: "100%",
          height: "100%",
          width: "100%",
          objectFit: "cover",
          objectPosition: "center",
          borderRadius: "0.29rem !important",
        }} />
      </Box>
    </Box>
  ))

  // Cleanup object URLs on unmount
  useEffect(
    () => () => {
      files.forEach((file) =>
        URL.revokeObjectURL(file ? file.preview : null),
      )
    },
    [files],
  )

  const handleChange = (event) => {
    const { name, value } = event.target
    const newData = cloneDeep(formData)
    set(newData, name, value)
    setFormData(newData)

    // Clear validation errors when user types
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: false,
      }))
    }
  }

  const handleTypeChange = (event, newValue) => {
    // Ensure newValue is always an array
    const typesArray = Array.isArray(newValue) ? newValue : []

    setFormData((prev) => ({
      ...prev,
      types: typesArray,
    }))
    // Clear validation error when user selects types
    if (!isEmpty(typesArray)) {
      setErrors((prev) => ({
        ...prev,
        types: false,
      }))
    }
  }

  const validateForm = () => {
    // Ensure types is always an array
    const typesArray = Array.isArray(formData.types)
      ? formData.types
      : []

    const newErrors = {
      name: !formData.name.trim(),
      types: isEmpty(typesArray),
      email:
        formData.email &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)}

    setErrors(newErrors)
    return !Object.values(newErrors).some(Boolean)
  }

  const optionalSections = [
    {
      sectionName: 'Buyer',
      fields: [
        <Grid size={12} key="buyer-criteria">
          <TextField
            name="optionalFields.buyer.criteria"
            label="Criteria"
            fullWidth
            value={formData?.optionalFields?.buyer?.criteria}
            onChange={handleChange}
          />
        </Grid>,
        <Grid size={12} key="buyer-pricePoint">
          <TextField
            name="optionalFields.buyer.pricePoint"
            label="Price Point"
            fullWidth
            value={formData?.optionalFields?.buyer?.pricePoint}
            onChange={handleChange}
          />
        </Grid>,
      ]},
    {
      sectionName: 'Hard Money Lender',
      fields: [
        <Grid size={12} key="hardMoneyLender-loanAmount">
          <TextField
            name="optionalFields.hardMoneyLender.loanAmount"
            label="Loan Amount"
            fullWidth
            value={
              formData?.optionalFields?.hardMoneyLender?.loanAmount
            }
            onChange={handleChange}
          />
        </Grid>,
        <Grid size={12} key="hardMoneyLender-points">
          <TextField
            name="optionalFields.hardMoneyLender.points"
            label="Points"
            fullWidth
            value={formData?.optionalFields?.hardMoneyLender?.points}
            onChange={handleChange}
          />
        </Grid>,
        <Grid size={12} key="hardMoneyLender-interest">
          <TextField
            name="optionalFields.hardMoneyLender.interest"
            label="Interest"
            fullWidth
            value={
              formData?.optionalFields?.hardMoneyLender?.interest
            }
            onChange={handleChange}
          />
        </Grid>,
        <Grid size={12} key="hardMoneyLender-junkFees">
          <TextField
            name="optionalFields.hardMoneyLender.junkFees"
            label="Junk Fees"
            fullWidth
            value={
              formData?.optionalFields?.hardMoneyLender?.junkFees
            }
            onChange={handleChange}
          />
        </Grid>,
        <Grid size={12} key="hardMoneyLender-term">
          <TextField
            name="optionalFields.hardMoneyLender.term"
            label="Term"
            fullWidth
            value={formData?.optionalFields?.hardMoneyLender?.term}
            onChange={handleChange}
          />
        </Grid>,
        <Grid size={12} key="hardMoneyLender-costExtension">
          <TextField
            name="optionalFields.hardMoneyLender.costExtension"
            label="Cost of Extension"
            fullWidth
            value={
              formData?.optionalFields?.hardMoneyLender?.costExtension
            }
            onChange={handleChange}
          />
        </Grid>,
        <Grid size={12} key="hardMoneyLender-prepaymentPenalty">
          <TextField
            name="optionalFields.hardMoneyLender.prepaymentPenalty"
            label="Pre-payment Penalty"
            fullWidth
            value={
              formData?.optionalFields?.hardMoneyLender
                ?.prepaymentPenalty
            }
            onChange={handleChange}
          />
        </Grid>,
        <Grid size={12} key="hardMoneyLender-drawFees">
          <TextField
            name="optionalFields.hardMoneyLender.drawFees"
            label="Draw Fees"
            fullWidth
            value={
              formData?.optionalFields?.hardMoneyLender?.drawFees
            }
            onChange={handleChange}
          />
        </Grid>,
        <Grid size={12}
          key="hardMoneyLender-reimburseConstructionCosts"
        >
          <TextField
            name="optionalFields.hardMoneyLender.reimburseConstructionCosts"
            label="Reimburse or Advance Construction Costs"
            fullWidth
            value={
              formData?.optionalFields?.hardMoneyLender
                ?.reimburseConstructionCosts
            }
            onChange={handleChange}
          />
        </Grid>,
        <Grid size={12} key="hardMoneyLender-timeFrameForDraw">
          <TextField
            name="optionalFields.hardMoneyLender.timeFrameForDraw"
            label="Time Frame for Draw Request"
            fullWidth
            value={
              formData?.optionalFields?.hardMoneyLender
                ?.timeFrameForDraw
            }
            onChange={handleChange}
          />
        </Grid>,

        <Grid size={12} key="hardMoneyLender-pullCredit">
          <TextField
            name="optionalFields.hardMoneyLender.pullCredit"
            label="Pull Credit"
            fullWidth
            value={
              formData?.optionalFields?.hardMoneyLender?.pullCredit
            }
            onChange={handleChange}
          />
        </Grid>,
        <Grid size={12} key="hardMoneyLender-minCreditScore">
          <TextField
            name="optionalFields.hardMoneyLender.minCreditScore"
            label="Minimum Credit Score"
            fullWidth
            value={
              formData?.optionalFields?.hardMoneyLender
                ?.minCreditScore
            }
            onChange={handleChange}
          />
        </Grid>,
        <Grid size={12} key="hardMoneyLender-infoRequired">
          <TextField
            name="optionalFields.hardMoneyLender.infoRequired"
            label="Info Requuired"
            fullWidth
            value={
              formData?.optionalFields?.hardMoneyLender?.infoRequired
            }
            onChange={handleChange}
          />
        </Grid>,
        <Grid size={12} key="hardMoneyLender-daysToClose">
          <TextField
            name="optionalFields.hardMoneyLender.daysToClose"
            label="How Many Days to Close"
            fullWidth
            value={
              formData?.optionalFields?.hardMoneyLender?.daysToClose
            }
            onChange={handleChange}
          />
        </Grid>,
        <Grid size={12} key="hardMoneyLender-dateOfContact">
          <TextField
            name="optionalFields.hardMoneyLender.dateOfContact"
            label="Date of Contact"
            fullWidth
            value={
              formData?.optionalFields?.hardMoneyLender?.dateOfContact
            }
            onChange={handleChange}
          />
        </Grid>,
        <Grid size={12} key="hardMoneyLender-directLenderBroker">
          <TextField
            name="optionalFields.hardMoneyLender.directLenderBroker"
            label="Direct Lender or Broker"
            fullWidth
            value={
              formData?.optionalFields?.hardMoneyLender
                ?.directLenderBroker
            }
            onChange={handleChange}
          />
        </Grid>,
        <Grid size={12} key="hardMoneyLender-qrp">
          <TextField
            name="optionalFields.hardMoneyLender.qrp"
            label="Do they lend to QRP's"
            fullWidth
            value={formData?.optionalFields?.hardMoneyLender?.qrp}
            onChange={handleChange}
          />
        </Grid>,
        <Grid size={12} key="hardMoneyLender-changeTerm">
          <TextField
            name="optionalFields.hardMoneyLender.changeTerm"
            label="If yes does it change their terms and rates? What Changes?"
            fullWidth
            multiline
            rows={3}
            value={
              formData?.optionalFields?.hardMoneyLender?.changeTerm
            }
            onChange={handleChange}
          />
        </Grid>,
        <Grid size={12} key="hardMoneyLender-percentageOfARV">
          <TextField
            name="optionalFields.hardMoneyLender.percentageOfARV"
            label="Do they lend on a percentage of ARV? If so what percentage?"
            fullWidth
            multiline
            rows={3}
            value={
              formData?.optionalFields?.hardMoneyLender
                ?.percentageOfARV
            }
            onChange={handleChange}
          />
        </Grid>,
        <Grid size={12} key="hardMoneyLender-percentageComplex">
          <TextField
            name="optionalFields.hardMoneyLender.percentageComplex"
            label="Do they lend on a percentage of purchase price and a percentage of rehab? If so what percentages?"
            fullWidth
            multiline
            rows={3}
            value={
              formData?.optionalFields?.hardMoneyLender
                ?.percentageComplex
            }
            onChange={handleChange}
          />
        </Grid>,
        <Grid size={12} key="hardMoneyLender-percentagePurchase">
          <TextField
            name="optionalFields.hardMoneyLender.percentagePurchase"
            label="Do they lend on a percentage of purchase price? If so what percentage?"
            fullWidth
            multiline
            rows={3}
            value={
              formData?.optionalFields?.hardMoneyLender
                ?.percentagePurchase
            }
            onChange={handleChange}
          />
        </Grid>,

        // Questionnaire responses (display/edit saved answers)
        // <Grid size={12} key="hmlq-fixFlip">
        //   <TextField
          //     name="optionalFields.hardMoneyLenderQuestionnaire.fixFlip"
          //     label="Intro Message"
          //     fullWidth
          //     multiline
          //     rows={3}
          //     value={
          //       formData?.optionalFields?.hardMoneyLenderQuestionnaire
          //         ?.fixFlip
          //     }
          //     onChange={handleChange}
          //   />
          // </Grid>,
          // <Grid size={12} key="hmlq-areas">
          //   <TextField
          //     name="optionalFields.hardMoneyLenderQuestionnaire.areas"
          //     label="What areas do you lend in?"
          //     fullWidth
          //     multiline
          //     rows={3}
          //     value={
          //       formData?.optionalFields?.hardMoneyLenderQuestionnaire
          //         ?.areas
          //     }
          //     onChange={handleChange}
          //   />
          // </Grid>,
          // <Grid size={12} key="hmlq-percentOf">
          //   <TextField
          //     name="optionalFields.hardMoneyLenderQuestionnaire.percentOf"
          //     label="Do you lend on % of ARV / % of Purchase + Rehab / Purchase Only?"
          //     fullWidth
          //     multiline
          //     rows={3}
          //     value={
          //       formData?.optionalFields?.hardMoneyLenderQuestionnaire
          //         ?.percentOf
          //     }
          //     onChange={handleChange}
          //   />
          // </Grid>,
          // <Grid size={12} key="hmlq-interest">
          //   <TextField
          //     name="optionalFields.hardMoneyLenderQuestionnaire.interest"
          //     label="Interest % Rates"
          //     fullWidth
          //     multiline
          //     rows={3}
          //     value={
          //       formData?.optionalFields?.hardMoneyLenderQuestionnaire
          //         ?.interest
          //     }
          //     onChange={handleChange}
          //   />
          // </Grid>,
          // <Grid size={12} key="hmlq-points">
          //   <TextField
          //     name="optionalFields.hardMoneyLenderQuestionnaire.points"
          //     label="Points (Origination Fees)"
          //     fullWidth
          //     multiline
          //     rows={3}
          //     value={
          //       formData?.optionalFields?.hardMoneyLenderQuestionnaire
          //         ?.points
          //     }
          //     onChange={handleChange}
          //   />
          // </Grid>,
          // <Grid size={12} key="hmlq-junkFee">
          //   <TextField
          //     name="optionalFields.hardMoneyLenderQuestionnaire.junkFee"
          //     label="Additional Closing Costs (Junk Fees)"
          //     fullWidth
          //     multiline
          //     rows={3}
          //     value={
          //       formData?.optionalFields?.hardMoneyLenderQuestionnaire
          //         ?.junkFee
          //     }
          //     onChange={handleChange}
          //   />
          // </Grid>,
          // <Grid size={12} key="hmlq-prePayPenalty">
          //   <TextField
          //     name="optionalFields.hardMoneyLenderQuestionnaire.prePayPenalty"
          //     label="Pre-payment Penalty"
          //     fullWidth
          //     multiline
          //     rows={3}
          //     value={
          //       formData?.optionalFields?.hardMoneyLenderQuestionnaire
          //         ?.prePayPenalty
          //     }
          //     onChange={handleChange}
          //   />
          // </Grid>,
          // <Grid size={12} key="hmlq-inspectionFee">
          //   <TextField
          //     name="optionalFields.hardMoneyLenderQuestionnaire.inspectionFee"
          //     label="Inspection / Draw Fees"
          //     fullWidth
          //     multiline
          //     rows={3}
          //     value={
          //       formData?.optionalFields?.hardMoneyLenderQuestionnaire
          //         ?.inspectionFee
          //     }
          //     onChange={handleChange}
          //   />
          // </Grid>,
          // <Grid size={12} key="hmlq-term">
          //   <TextField
          //     name="optionalFields.hardMoneyLenderQuestionnaire.term"
          //     label="Term"
          //     fullWidth
          //     multiline
          //     rows={3}
          //     value={
          //       formData?.optionalFields?.hardMoneyLenderQuestionnaire
          //         ?.term
          //     }
          //     onChange={handleChange}
          //   />
          // </Grid>,
          // <Grid size={12} key="hmlq-extensionCost">
          //   <TextField
          //     name="optionalFields.hardMoneyLenderQuestionnaire.extensionCost"
          //     label="Cost of Extension"
          //     fullWidth
          //     multiline
          //     rows={3}
          //     value={
          //       formData?.optionalFields?.hardMoneyLenderQuestionnaire
          //         ?.extensionCost
          //     }
          //     onChange={handleChange}
          //   />
          // </Grid>,
          // <Grid size={12} key="hmlq-constructionCost">
          //   <TextField
          //     name="optionalFields.hardMoneyLenderQuestionnaire.constructionCost"
          //     label="Advance or Reimburse Construction Costs?"
          //     fullWidth
          //     multiline
          //     rows={3}
          //     value={
          //       formData?.optionalFields?.hardMoneyLenderQuestionnaire
          //         ?.constructionCost
          //     }
          //     onChange={handleChange}
          //   />
          // </Grid>,
          // <Grid size={12} key="hmlq-drawTime">
          //   <TextField
          //     name="optionalFields.hardMoneyLenderQuestionnaire.drawTime"
          //     label="Typical timeframe before draw request to payment"
          //     fullWidth
          //     multiline
          //     rows={3}
          //     value={
          //       formData?.optionalFields?.hardMoneyLenderQuestionnaire
          //         ?.drawTime
          //     }
          //     onChange={handleChange}
          //   />
          // </Grid>,
          // <Grid size={12} key="hmlq-pullCredit">
          //   <TextField
          //     name="optionalFields.hardMoneyLenderQuestionnaire.pullCredit"
          //     label="Do you pull credit?"
          //     fullWidth
          //     multiline
          //     rows={3}
          //     value={
          //       formData?.optionalFields?.hardMoneyLenderQuestionnaire
          //         ?.pullCredit
          //     }
          //     onChange={handleChange}
          //   />
          // </Grid>,
          // <Grid size={12} key="hmlq-minimumCreditScore">
          //   <TextField
          //     name="optionalFields.hardMoneyLenderQuestionnaire.minimumCreditScore"
          //     label="Minimum credit score requirement"
          //     fullWidth
          //     multiline
          //     rows={3}
          //     value={
          //       formData?.optionalFields?.hardMoneyLenderQuestionnaire
          //         ?.minimumCreditScore
          //     }
          //     onChange={handleChange}
          //   />
          // </Grid>,
          // <Grid size={12} key="hmlq-dealInfo">
          //   <TextField
          //     name="optionalFields.hardMoneyLenderQuestionnaire.dealInfo"
          //     label="Info required to make a decision"
          //     fullWidth
          //     multiline
          //     rows={3}
          //     value={
          //       formData?.optionalFields?.hardMoneyLenderQuestionnaire
          //         ?.dealInfo
          //     }
          //     onChange={handleChange}
          //   />
          // </Grid>,
          // <Grid size={12} key="hmlq-timeToClose">
          //   <TextField
          //     name="optionalFields.hardMoneyLenderQuestionnaire.timeToClose"
          //     label="Days to close"
          //     fullWidth
          //     multiline
          //     rows={3}
          //     value={
          //       formData?.optionalFields?.hardMoneyLenderQuestionnaire
          //         ?.timeToClose
          //     }
          //     onChange={handleChange}
          //   />
          // </Grid>,
          // <Grid size={12} key="hmlq-lenderOrBroker">
          //   <TextField
          //     name="optionalFields.hardMoneyLenderQuestionnaire.lenderOrBroker"
          //     label="Direct lender or broker?"
          //     fullWidth
          //     multiline
          //     rows={3}
          //     value={
          //       formData?.optionalFields?.hardMoneyLenderQuestionnaire
          //         ?.lenderOrBroker
          //     }
          //     onChange={handleChange}
          //   />
          // </Grid>,
          // <Grid size={12} key="hmlq-retirementFund">
          //   <TextField
          //     name="optionalFields.hardMoneyLenderQuestionnaire.retirementFund"
          //     label="Can funds come from self-directed IRA or solo 401k?"
          //     fullWidth
          //     multiline
          //     rows={3}
          //     value={
          //       formData?.optionalFields?.hardMoneyLenderQuestionnaire
          //         ?.retirementFund
          //     }
          //     onChange={handleChange}
          //   />
          // </Grid>,
          // <Grid size={12} key="hmlq-retirementFundChanges">
          //   <TextField
          //     name="optionalFields.hardMoneyLenderQuestionnaire.retirementFundChanges"
          //     label="If yes, does this change amounts/rates/points?"
          //     fullWidth
          //     multiline
          //     rows={3}
          //     value={
          //       formData?.optionalFields?.hardMoneyLenderQuestionnaire
          //         ?.retirementFundChanges
          //     }
          //     onChange={handleChange}
          //   />
          // </Grid>,
          // <Grid size={12} key="hmlq-nextStepEmail">
          //   <TextField
          //     name="optionalFields.hardMoneyLenderQuestionnaire.nextStepEmail"
          //     label="Email next steps to get started"
          //     fullWidth
          //     multiline
          //     rows={3}
          //     value={
          //       formData?.optionalFields?.hardMoneyLenderQuestionnaire
          //         ?.nextStepEmail
          //     }
          //     onChange={handleChange}
          //   />
          // </Grid>,
          ]},
          {
          sectionName: 'Property Manager',
          fields: [
          // Questionnaire responses (display/edit saved answers)
          <Grid size={12} key="propertyManager-services">
          <TextField
          name="optionalFields.propertyManager.services"
          label="What are the various services that you offer to your clients?"
          fullWidth
          multiline
          rows={3}
          value={
          formData?.optionalFields?.propertyManager?.services
          }
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-unitCount">
          <TextField
          name="optionalFields.propertyManager.unitCount"
          label="How many rental units do you manage?"
          fullWidth
          multiline
          rows={3}
          value={
          formData?.optionalFields?.propertyManager?.unitCount
          }
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-experience">
          <TextField
          name="optionalFields.propertyManager.experience"
          label="What experience does your company owner have in managing rentals?"
          fullWidth
          multiline
          rows={3}
          value={
          formData?.optionalFields?.propertyManager?.experience
          }
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-determineRent">
          <TextField
          name="optionalFields.propertyManager.determineRent"
          label="How do you determine rent amount?"
          fullWidth
          multiline
          rows={3}
          value={
          formData?.optionalFields?.propertyManager?.determineRent
          }
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-isActive">
          <TextField
          name="optionalFields.propertyManager.isActive"
          label="Are you currently an active real estate investor in your market?"
          fullWidth
          multiline
          rows={3}
          value={
          formData?.optionalFields?.propertyManager?.isActive
          }
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-cancelCondition">
          <TextField
          name="optionalFields.propertyManager.cancelCondition"
          label="Under what conditions can I cancel my management contract"
          fullWidth
          multiline
          rows={3}
          value={
          formData?.optionalFields?.propertyManager
          ?.cancelCondition
          }
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-fees">
          <TextField
          name="optionalFields.propertyManager.fees"
          label="What are the management fees and/or pricing options when the property is being rented?"
          fullWidth
          multiline
          rows={3}
          value={formData?.optionalFields?.propertyManager?.fees}
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-vacancyFees">
          <TextField
          name="optionalFields.propertyManager.vacancyFees"
          label="Are there fees when the property has no tenants?"
          fullWidth
          multiline
          rows={3}
          value={
          formData?.optionalFields?.propertyManager?.vacancyFees
          }
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-miscFees">
          <TextField
          name="optionalFields.propertyManager.miscFees"
          label="What miscellaneous fees could I be charged for the management of my property?"
          fullWidth
          multiline
          rows={3}
          value={
          formData?.optionalFields?.propertyManager?.miscFees
          }
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-exclusive">
          <TextField
          name="optionalFields.propertyManager.exclusive"
          label="Do I have to sell my property with you if I want to list it?"
          fullWidth
          multiline
          rows={3}
          value={
          formData?.optionalFields?.propertyManager?.exclusive
          }
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-directDeposit">
          <TextField
          name="optionalFields.propertyManager.directDeposit"
          label="Do you offer direct deposit for your owners?"
          fullWidth
          multiline
          rows={3}
          value={
          formData?.optionalFields?.propertyManager?.directDeposit
          }
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-rentMethod">
          <TextField
          name="optionalFields.propertyManager.rentMethod"
          label="How do you collect rent from your tenants?"
          fullWidth
          multiline
          rows={3}
          value={
          formData?.optionalFields?.propertyManager?.rentMethod
          }
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-inspections">
          <TextField
          name="optionalFields.propertyManager.inspections"
          label="Do you conduct property inspections (how often) and, if you do, what charge is associated with them?"
          fullWidth
          multiline
          rows={3}
          value={
          formData?.optionalFields?.propertyManager?.inspections
          }
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-responsibility">
          <TextField
          name="optionalFields.propertyManager.responsibility"
          label="What is your property manager’s responsibility versus what is yours?"
          fullWidth
          multiline
          rows={3}
          value={
          formData?.optionalFields?.propertyManager
          ?.responsibility
          }
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-futureSteps">
          <TextField
          name="optionalFields.propertyManager.futureSteps"
          label="What steps do you take to market properties?"
          fullWidth
          multiline
          rows={3}
          value={
          formData?.optionalFields?.propertyManager?.futureSteps
          }
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-vacancyLength">
          <TextField
          name="optionalFields.propertyManager.vacancyLength"
          label="How long are your properties typically vacant?"
          fullWidth
          multiline
          rows={3}
          value={
          formData?.optionalFields?.propertyManager?.vacancyLength
          }
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12}
          key="propertyManager-applicantRequirements"
          >
          <TextField
          name="optionalFields.propertyManager.applicantRequirements"
          label="What are your income and screening requirements for applicants?"
          fullWidth
          multiline
          rows={3}
          value={
          formData?.optionalFields?.propertyManager
          ?.applicantRequirements
          }
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-leaseControl">
          <TextField
          name="optionalFields.propertyManager.leaseControl"
          label="What control do I have over the tenant lease agreements?"
          fullWidth
          multiline
          rows={3}
          value={
          formData?.optionalFields?.propertyManager?.leaseControl
          }
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-maintenanceMarkup">
          <TextField
          name="optionalFields.propertyManager.maintenanceMarkup"
          label="Do you mark-up maintenance and repairs?"
          fullWidth
          multiline
          rows={3}
          value={
          formData?.optionalFields?.propertyManager
          ?.maintenanceMarkup
          }
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-portfolioUpdate">
          <TextField
          name="optionalFields.propertyManager.portfolioUpdate"
          label="How often will I get updates on my portfolio?"
          fullWidth
          multiline
          rows={3}
          value={
          formData?.optionalFields?.propertyManager
          ?.portfolioUpdate
          }
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-petCost">
          <TextField
          name="optionalFields.propertyManager.petCost"
          label="How much do you charge for cats/dogs?"
          fullWidth
          multiline
          rows={3}
          value={formData?.optionalFields?.propertyManager?.petCost}
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-washerDryer">
          <TextField
          name="optionalFields.propertyManager.washerDryer"
          label="What are your thoughts on our washer/dryer area? (optional: If you have washer/dryers or have a community area)"
          fullWidth
          multiline
          rows={3}
          value={
          formData?.optionalFields?.propertyManager?.washerDryer
          }
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-notes">
          <TextField
          name="optionalFields.propertyManager.notes"
          label="Notes"
          fullWidth
          multiline
          rows={5}
          value={formData?.optionalFields?.propertyManager?.notes}
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-questions">
          <TextField
          name="optionalFields.propertyManager.questions"
          label="Any Questions to ask them about your specific property?"
          fullWidth
          multiline
          rows={5}
          value={
          formData?.optionalFields?.propertyManager?.questions
          }
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-fixedup">
          <TextField
          name="optionalFields.propertyManager.fixedup"
          label="What do you think it would rent for once fixed up?"
          fullWidth
          multiline
          rows={5}
          value={formData?.optionalFields?.propertyManager?.fixedup}
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-timeTilRent">
          <TextField
          name="optionalFields.propertyManager.timeTilRent"
          label="How long do you think it would take to rent? "
          fullWidth
          multiline
          rows={5}
          value={
          formData?.optionalFields?.propertyManager?.timeTilRent
          }
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-areaInfo">
          <TextField
          name="optionalFields.propertyManager.areaInfo"
          label="NoteAnything I should know about this area?s"
          fullWidth
          multiline
          rows={5}
          value={
          formData?.optionalFields?.propertyManager?.areaInfo
          }
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="propertyManager-missedQuestions">
          <TextField
          name="optionalFields.propertyManager.missedQuestions"
          label="Any questions that maybe I didn't ask that I should have asked?"
          fullWidth
          multiline
          rows={5}
          value={
          formData?.optionalFields?.propertyManager
          ?.missedQuestions
          }
          onChange={handleChange}
          />
          </Grid>,
          ]},
          {
          sectionName: 'Contractor',
          fields: [
          <Grid size={12} key="contractor-licensed">
          <FormControl fullWidth>
          <InputLabel>Licensed</InputLabel>
          <Select
          name="optionalFields.contractor.licensed"
          value={formData?.optionalFields?.contractor?.licensed}
          onChange={handleChange}
          label="Licensed"
          >
          {['Yes', 'No'].map((type) => (
          <MenuItem key={type} value={type}>
          {type}
          </MenuItem>
          ))}
          </Select>
          </FormControl>
          </Grid>,
          <Grid size={12} key="contractor-bonded">
          <FormControl fullWidth>
          <InputLabel>Bonded</InputLabel>
          <Select
          name="optionalFields.contractor.bonded"
          value={formData?.optionalFields?.contractor?.bonded}
          onChange={handleChange}
          label="Bonded"
          >
          {['Yes', 'No'].map((type) => (
          <MenuItem key={type} value={type}>
          {type}
          </MenuItem>
          ))}
          </Select>
          </FormControl>
          </Grid>,
          <Grid size={12} key="contractor-insured">
          <FormControl fullWidth>
          <InputLabel>Insured</InputLabel>
          <Select
          name="optionalFields.contractor.insured"
          value={formData?.optionalFields?.contractor?.insured}
          onChange={handleChange}
          label="Insured"
          >
          {['Yes', 'No'].map((type) => (
          <MenuItem key={type} value={type}>
          {type}
          </MenuItem>
          ))}
          </Select>
          </FormControl>
          </Grid>,
          <Grid size={12} key="contractor-verbalBidWalkProperty">
          <TextField
          name="optionalFields.contractor.verbalBidWalkProperty"
          label="Verbal Bid/Walk Property"
          fullWidth
          value={
          formData?.optionalFields?.contractor
          ?.verbalBidWalkProperty
          }
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="contractor-itemizedBid">
          <TextField
          name="optionalFields.contractor.itemizedBid"
          label="Itemized Bid"
          fullWidth
          value={formData?.optionalFields?.contractor?.itemizedBid}
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="contractor-maxJobs">
          <TextField
          name="optionalFields.contractor.maxJobs"
          label="Max Jobs"
          fullWidth
          value={formData?.optionalFields?.contractor?.maxJobs}
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="contractor-website">
          <TextField
          name="optionalFields.contractor.website"
          label="Website"
          fullWidth
          value={formData?.optionalFields?.contractor?.website}
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="contractor-howDidWeFind">
          <TextField
          name="optionalFields.contractor.howDidWeFind"
          label="How did we find them?"
          fullWidth
          value={formData?.optionalFields?.contractor?.howDidWeFind}
          onChange={handleChange}
          />
          </Grid>,
          ]},
          {
          sectionName: 'Inspector',
          fields: [
          <Grid size={12} key="inspector-website">
          <TextField
          name="optionalFields.inspector.website"
          label="Website"
          fullWidth
          value={formData?.optionalFields?.inspector?.website}
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="inspector-certified">
          <FormControl fullWidth>
          <InputLabel>ASHI Certified</InputLabel>
          <Select
          name="optionalFields.inspector.certified"
          value={formData?.optionalFields?.inspector?.certified}
          onChange={handleChange}
          label="ASHI Certified"
          >
          {['Yes', 'No'].map((type) => (
          <MenuItem key={type} value={type}>
          {type}
          </MenuItem>
          ))}
          </Select>
          </FormControl>
          </Grid>,
          <Grid size={12} key="inspector-inspectionNumber">
          <TextField
          name="optionalFields.inspector.inspectionNumber"
          label="# of Inspections Performed"
          fullWidth
          value={
          formData?.optionalFields?.inspector?.inspectionNumber
          }
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="inspector-quickResponse">
          <TextField
          name="optionalFields.inspector.quickResponse"
          label="How Quickly can they get out to see it?"
          fullWidth
          value={formData?.optionalFields?.inspector?.quickResponse}
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="inspector-turnAround">
          <TextField
          name="optionalFields.inspector.turnAround"
          label="Turn Around Time"
          fullWidth
          value={formData?.optionalFields?.inspector?.turnAround}
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="inspector-cost">
          <TextField
          name="optionalFields.inspector.cost"
          label="Cost"
          fullWidth
          value={formData?.optionalFields?.inspector?.cost}
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="inspector-howDidWeFind">
          <TextField
          name="optionalFields.inspector.howDidWeFind"
          label="How did we find them?"
          fullWidth
          value={formData?.optionalFields?.inspector?.howDidWeFind}
          onChange={handleChange}
          />
          </Grid>,
          ]},
          {
          sectionName: 'Title Company',
          fields: [
          <Grid size={12} key="titleCompany-website">
          <TextField
          name="optionalFields.titleCompany.website"
          label="Website"
          fullWidth
          value={formData?.optionalFields?.titleCompany?.website}
          onChange={handleChange}
          />
          </Grid>,
          <Grid size={12} key="titleCompany-doubleClose">
          <FormControl fullWidth>
          <InputLabel>Double Close</InputLabel>
          <Select
          name="optionalFields.titleCompany.doubleClose"
          value={
          formData?.optionalFields?.titleCompany?.doubleClose
          }
          onChange={handleChange}
          label="Double Close"
          >
          {['Yes', 'No'].map((type) => (
          <MenuItem key={type} value={type}>
          {type}
          </MenuItem>
          ))}
          </Select>
          </FormControl>
          </Grid>,
          ]},
          ]
          const handleSubmit = async (event) => {
          event.preventDefault()
          // Validate form
          if (!validateForm()) {
          return
          }
          setLoading(true)
          try {
          await onSave(formData, changedFile ? files : [])
          // Reset initial data after successful save
          setInitialFormData(formData)
          setInitialFiles(changedFile ? files : [])
          setChangedFile(false)
          onClose()
          } catch (error) {
          console.error('Error saving contact:', error)
          } finally {
          setLoading(false)
          }
          }
          return (
          <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (showConfirmClose && reason === 'escapeKeyDown') {
            return
          }
          handleCloseAttempt()
        }}
        maxWidth="sm"
        fullWidth
      >
        <BlockUi
          blocking={loading}
          loader={
            <PacmanLoader color={'var(--primary)'} loading={true} />
          }
          message={
            <Box sx={{ color: "#368cff" }}>
              {contact ? 'Updating Contact' : 'Adding Contact'}
            </Box>
          }
        >
          <form onSubmit={handleSubmit}>
            <DialogTitle>
              {contact ? 'Edit Contact' : 'Add New Contact'}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid size={12}>
                  <TextField
                    name="name"
                    label="Name"
                    fullWidth
                    required
                    value={formData.name}
                    onChange={handleChange}
                    error={errors.name}
                    helperText={
                      errors.name ? 'Please fill out this field.' : ''
                    }
                  />
                </Grid>
                <Grid size={12}>
                  <Box sx={{
                    py: "1.5rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                    <Box data-hf="dropzone" sx={{
                      "html[data-color-mode=\"dark\"] &": { backgroundColor: "#2a2a2a !important", borderColor: "rgba(255, 255, 255, 0.28) !important", color: "rgba(255, 255, 255, 0.7) !important" },
                      borderRadius: "0.65rem !important",
                      boxShadow: "0 0.18rem 0.3rem rgba(166, 166, 185, 0.5), 0 0.326rem 3rem rgba(79, 80, 98, 0.3) !important",
                    }}>
                      <Box
                        {...getRootProps()} sx={{
                          position: "relative",
                          display: "flex",
                          flexDirection: "column",
                          outline: "none !important",
                          "& *": { outline: "none !important" },
                        }}
                      >
                        <input {...getInputProps()} />
                        <Box data-hf="dropzone-inner" sx={{
                          padding: "3rem 1rem",
                          cursor: "initial",
                          transition: "all 0.2s ease-in-out",
                          textAlign: "center",
                          color: "#a6a6b9",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "column",
                          border: "2px dashed #e6e7f1",
                          borderRadius: "0.65rem !important",
                          backgroundColor: "#f8f9ff",
                          "&:hover": { borderColor: "#368cff", color: "#4f5062" },
                          "html[data-color-mode=\"dark\"] &": { backgroundColor: "#2a2a2a !important", borderColor: "rgba(255, 255, 255, 0.28) !important", color: "rgba(255, 255, 255, 0.7) !important" },
                          "html[data-color-mode=\"dark\"] &:hover": { borderColor: "#368cff !important", color: "rgba(255, 255, 255, 0.87) !important", backgroundColor: "#333 !important" },
                          height: "140px !important",
                          width: "140px !important",
                          lineHeight: "140px !important",
                          p: 0,
                          "& [data-hf=\"avatar-image\"], & .avatar-image": {
                            position: "absolute",
                            left: 0,
                            top: 0,
                            zIndex: 4,
                            width: "100%",
                            height: "100%",
                            overflow: "hidden",
                            border: 0,
                          },
                          "& .avatar-button, & [data-hf=\"avatar-button\"]": { zIndex: 5 },
                        }}>
                          <Box sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: "0.25rem",
                            position: "relative",
                            fontSize: "1.2rem",
                            transition: "all 0.2s ease-in-out",
                            "&:last-child": { mr: 0, marginRight: 0 },
                            "& .badge": { position: "absolute", right: 0, bottom: 0 },
                            "& svg, & .svg-inline--fa": { display: "block" },
                            marginRight: "0.25rem",
                            height: "140px !important",
                            width: "140px !important",
                            lineHeight: "140px !important",
                            p: 0,
                            borderRadius: "0.65rem !important",
                            m: "0.5rem",
                          }}>
                            <Tooltip title="Upload Contact Photo">
                              {isEmpty(thumbs) ? (
                                <Button onClick={openDropzone} sx={{
                                  display: "inline-flex",
                                  py: 0,
                                  px: "0.7em",
                                  fontSize: "70%",
                                  fontWeight: 700,
                                  lineHeight: "40px !important",
                                  textAlign: "center",
                                  whiteSpace: "nowrap",
                                  verticalAlign: "baseline",
                                  borderRadius: "100%",
                                  textTransform: "uppercase",
                                  height: 10,
                                  boxShadow: "0 0.25rem 0.55rem rgba(65, 145, 255, 0.35)",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: 0,
                                  position: "absolute !important",
                                  right: 0,
                                  bottom: 0,
                                  top: "auto",
                                  zIndex: 2,
                                  textIndent: "-999rem",
                                  width: 10,
                                  p: 0,
                                  color: "#fff",
                                  backgroundColor: "#4191ff",
                                  backgroundImage: "none",
                                  borderColor: "#4191ff",
                                  "&:not(:disabled):hover": { backgroundColor: "#4191ff", color: "#fff" },
                                  "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": { backgroundColor: "#0e73ff" },
                                  "&:not(:disabled):active": { boxShadow: "0 0.22rem 0.525rem rgba(65, 145, 255, 0.4), 0 0.0625rem 0.385rem rgba(65, 145, 255, 0.54)" },
                                  "&:not(:disabled).active .btn-wrapper--icon svg path, &:hover .btn-wrapper--icon svg path": { fill: "#fff" },
                                }}>
                                  <CameraAltTwoToneIcon sx={{
                                    height: "20px !important",
                                    width: "20px !important",
                                    lineHeight: "20px !important",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    p: 0,
                                  }} />
                                </Button>
                              ) : (
                                <Button onClick={ handleEditImageMoreVertOpen } sx={{
                                  display: "inline-flex",
                                  py: 0,
                                  px: "0.7em",
                                  fontSize: "70%",
                                  fontWeight: 700,
                                  lineHeight: "40px !important",
                                  textAlign: "center",
                                  whiteSpace: "nowrap",
                                  verticalAlign: "baseline",
                                  borderRadius: "100%",
                                  textTransform: "uppercase",
                                  height: 10,
                                  boxShadow: "0 0.25rem 0.55rem rgba(65, 145, 255, 0.35)",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: 0,
                                  position: "absolute !important",
                                  right: 0,
                                  bottom: 0,
                                  top: "auto",
                                  zIndex: 2,
                                  textIndent: "-999rem",
                                  width: 10,
                                  p: 0,
                                  color: "#fff",
                                  backgroundColor: "#4191ff",
                                  backgroundImage: "none",
                                  borderColor: "#4191ff",
                                  "&:not(:disabled):hover": { backgroundColor: "#4191ff", color: "#fff" },
                                  "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": { backgroundColor: "#0e73ff" },
                                  "&:not(:disabled):active": { boxShadow: "0 0.22rem 0.525rem rgba(65, 145, 255, 0.4), 0 0.0625rem 0.385rem rgba(65, 145, 255, 0.54)" },
                                  "&:not(:disabled).active .btn-wrapper--icon svg path, &:hover .btn-wrapper--icon svg path": { fill: "#fff" },
                                }}
                                  >
                                  <MoreVertIcon sx={{
                                    height: "20px !important",
                                    width: "20px !important",
                                    lineHeight: "20px !important",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    p: 0,
                                  }} />
                                </Button>
                              )}
                            </Tooltip>
                            <div>
                              {isDragAccept && (
                                <Box sx={{
                                  borderRadius: "0.65rem !important",
                                  overflow: "hidden !important",
                                  outline: "none !important",
                                  height: "140px !important",
                                  width: "140px !important",
                                  lineHeight: "140px !important",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  p: 0,
                                  backgroundColor: "#1bc943",
                                  textAlign: "center",
                                  fontWeight: 700,
                                  color: "#fff",
                                }}>
                                  <CheckIcon sx={{
                                    height: "40px !important",
                                    width: "40px !important",
                                    lineHeight: "40px !important",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    p: 0,
                                  }} />
                                </Box>
                              )}
                              {isDragReject && (
                                <Box sx={{
                                  borderRadius: "0.65rem !important",
                                  overflow: "hidden !important",
                                  outline: "none !important",
                                  height: "140px !important",
                                  width: "140px !important",
                                  lineHeight: "140px !important",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  p: 0,
                                  backgroundColor: "#f83245",
                                  textAlign: "center",
                                  fontWeight: 700,
                                  color: "#fff",
                                }}>
                                  <CloseTwoToneIcon sx={{
                                    height: "60px !important",
                                    width: "60px !important",
                                    lineHeight: "60px !important",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    p: 0,
                                  }} />
                                </Box>
                              )}
                              {!isDragActive && (
                                <Box sx={{
                                  borderRadius: "0.65rem !important",
                                  overflow: "hidden !important",
                                  outline: "none !important",
                                  height: "140px !important",
                                  width: "140px !important",
                                  lineHeight: "140px !important",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  p: 0,
                                  backgroundColor: "rgb(96.1617647059%, 96.1617647059%, 97.0147058824%) !important",
                                  textAlign: "center",
                                  fontWeight: 700,
                                  color: "rgba(59, 62, 102, 0.5)",
                                }}>
                                  <FontAwesomeIcon icon={['far', 'user']} sx={{
                                    height: "100px !important",
                                    width: "100px !important",
                                    lineHeight: "100px !important",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    p: 0,
                                  }} />
                                </Box>
                              )}
                            </div>

                            {thumbs.length > 0 && <div>{thumbs}</div>}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
                <Grid size={12}>
                  <Autocomplete
                    multiple
                    options={CONTACT_TYPES}
                    value={formData.types}
                    onChange={handleTypeChange}
                    isOptionEqualToValue={(option, value) =>
                      option === value
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Contact Type *"
                        fullWidth
                        error={errors.types}
                        helperText={
                          errors.types
                            ? 'Please fill out this field.'
                            : ''
                        }
                      />
                    )}
                    renderValue={(value, getItemProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getItemProps({ index })}
                          key={option}
                          label={option}
                          deleteIcon={<CloseTwoToneIcon />}
                          sx={{
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '& .MuiChip-deleteIcon': {
                              color: 'white',
                              '&:hover': {
                                color: 'error.main'}}}}
                        />
                      ))
                    }
                    renderOption={(props, option) => {
                      const { key, ...otherProps } = props
                      return (
                        <Box component="li" key={key} {...otherProps}>
                          <Typography
                            sx={{
                              backgroundColor:
                                formData.types.includes(option)
                                  ? 'rgba(25, 118, 210, 0.9) !important'
                                  : 'transparent',
                              color: formData.types.includes(option)
                                ? '#ffffff !important'
                                : 'inherit',
                              fontWeight: formData.types.includes(
                                option,
                              )
                                ? 'bold'
                                : 'normal',
                              padding: '8px 16px',
                              borderRadius: '4px',
                              width: '100%'}}
                          >
                            {option}
                          </Typography>
                        </Box>
                      )
                    }}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    name="email"
                    label="Email"
                    fullWidth
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={errors.email}
                    helperText={
                      errors.email
                        ? 'Please enter a valid email address.'
                        : ''
                    }
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    name="phone"
                    label="Phone"
                    fullWidth
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    name="company"
                    label="Company"
                    fullWidth
                    value={formData.company}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    name="notes"
                    label="Notes"
                    fullWidth
                    multiline
                    rows={3}
                    value={formData.notes}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    name="address"
                    label="Address"
                    fullWidth
                    value={formData.address}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    name="city"
                    label="City"
                    fullWidth
                    value={formData.city}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid size={12}>
                  <FormControl fullWidth>
                    <InputLabel>State</InputLabel>
                    <Select
                      name="state"
                      value={formData.state || ''}
                      onChange={handleChange}
                      label="State"
                    >
                      <MenuItem value="">
                        <em>Select a state</em>
                      </MenuItem>
                      {STATES.map((state) => (
                        <MenuItem
                          key={state.shortname}
                          value={state.shortname}
                        >
                          {state.name} ({state.shortname })
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={12}>
                  <TextField
                    name="area"
                    label="Area"
                    fullWidth
                    value={formData.area}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid size={12}>
                  <FormControl fullWidth>
                    <InputLabel>Ranking</InputLabel>
                    <Select
                      name="ranking"
                      value={formData.ranking ?? ''}
                      onChange={handleChange}
                      label="Ranking"
                    >
                      <MenuItem value="">
                        <em>Select a ranking</em>
                      </MenuItem>
                      {[
                        { value: 5, label: '5 - Excellent' },
                        { value: 4, label: '4 - Good' },
                        { value: 3, label: '3 - Average' },
                        { value: 2, label: '2 - Poor' },
                        { value: 1, label: '1 - Very Poor' },
                      ].map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={12}>
                  <Box sx={[{
                    "& > .MuiPaper-root": {
                      overflow: "hidden",
                      boxShadow: "none !important",
                      borderRadius: 0,
                      "&:first-of-type": { borderTopLeftRadius: "0.65rem", borderTopRightRadius: "0.65rem" },
                      "&:last-of-type": { borderBottomLeftRadius: "0.65rem", borderBottomRightRadius: "0.65rem" },
                      "&:not(:last-of-type)": { borderBottom: 0 },
                      "& > .MuiPaper-root": { boxShadow: "none !important", borderRadius: "inherit" },
                    },
                    "& .panel-title, & .accordion-toggle, & [data-hf=\"panel-title\"], & [data-hf=\"accordion-toggle\"]": { width: "100%", display: "block" },
                    "& .MuiCard-root > div:has(> .panel-title), & .MuiCard-root > div:has(> [data-hf=\"panel-title\"])": {
                      display: "block !important",
                      padding: "0 !important",
                      width: "100%",
                      backgroundColor: "transparent !important",
                    },
                    "& > .card": {
                      overflow: "hidden",
                      "&:not(:last-of-type)": { borderBottom: 0, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                      "&:not(:first-of-type)": { borderTopLeftRadius: 0, borderTopRightRadius: 0 },
                      "& > .card-header": { borderRadius: 0, marginBottom: 0 },
                    },
                  }, { width: '100%' }, { marginBottom: "48px" }]}>
                    {map(
                      filter(optionalSections, (section) =>
                        formData.types?.includes(section.sectionName),
                      ),
                      (section) => (
                        <Card variant="outlined" key={section.sectionName} sx={(openAccordion === section.sectionName) && {
                          "& [data-hf=\"accordion-toggle\"] .MuiButton-root": {
                            borderBottomColor: "rgba(79, 80, 98, 0.3)",
                            background: "#fff",
                            color: "#070919 !important",
                            "& [data-hf=\"accordion-icon\"]": { transform: "rotate(180deg)", color: "#368cff !important" },
                          },
                          "html[data-color-mode=\"dark\"] & [data-hf=\"accordion-toggle\"] .MuiButton-root": { borderBottomColor: "rgba(255, 255, 255, 0.12) !important", background: "#252525 !important", color: "rgba(255, 255, 255, 0.95) !important" },
                        }}>
                          <Card>
                            <Box sx={{
                              display: "flex",
                              alignItems: "center",
                              py: "0.75rem",
                              px: "1.25rem",
                              mb: 0,
                              backgroundColor: "#fff",
                              padding: "0.75rem 1.25rem",
                              marginBottom: 0,
                              borderBottom: "0 solid rgba(79, 80, 98, 0.3)",
                              "&:first-of-type": { borderRadius: "0.65rem 0.65rem 0 0" },
                              "html[data-color-mode=\"dark\"] &": { backgroundColor: "#252525 !important", color: "rgba(255, 255, 255, 0.87)", borderColor: "rgba(255, 255, 255, 0.12) !important" },
                            }}>
                              <Box data-hf="panel-title" sx={{ width: "100%", display: "block" }}>
                                <Box data-hf="accordion-toggle" sx={{
                                  "& .MuiButton-root": {
                                    display: "flex",
                                    width: "100%",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    textAlign: "left",
                                    fontWeight: 700,
                                    color: "#3b3e66 !important",
                                    textDecoration: "none !important",
                                    textTransform: "none",
                                    borderRadius: 0,
                                    padding: "0.75rem 1.25rem !important",
                                    borderBottom: "1px solid transparent",
                                    background: "#f4f5fd",
                                    "&:hover": {
                                      background: "#f8f9ff !important",
                                      color: "#070919 !important",
                                      textDecoration: "none !important",
                                      "& [data-hf=\"accordion-icon\"]": { color: "#368cff !important" },
                                    },
                                    "&:not(:disabled):hover": { transform: "translateY(0px)" },
                                    "& [data-hf=\"accordion-icon\"]": {
                                      transform: "rotate(90deg)",
                                      transition: "all 0.2s ease-in-out",
                                      marginLeft: "auto",
                                      color: "#368cff !important",
                                      overflow: "visible",
                                    },
                                  },
                                  "html[data-color-mode=\"dark\"] & .MuiButton-root": {
                                    background: "#2a2a2a !important",
                                    color: "rgba(255, 255, 255, 0.9) !important",
                                    "&:hover": { background: "#333 !important", color: "#fff !important" },
                                  },
                                }}>
                                  <Button
                                    variant="text"
                                    size="large"
                                    onClick={() =>
                                      toggleAccordion(
                                        section.sectionName,
                                      )
                                    }
                                    sx={{
                                      fontWeight: 700,
                                      color: "#3b3e66",
                                      textDecoration: "none",
                                      "&:hover": {
                                        backgroundColor: "transparent",
                                        color: "#4191ff",
                                        textDecoration: "underline",
                                        transform: "translateY(0px)",
                                      },
                                      "&:active": { textDecoration: "underline", boxShadow: "none" },
                                      "&:disabled": { color: "#7a7b97", pointerEvents: "none" },
                                      "& span": {
                                        position: "relative",
                                        "&::before": {
                                          content: "\"\"",
                                          position: "absolute",
                                          width: "100%",
                                          height: 2,
                                          bottom: 0,
                                          left: 0,
                                          visibility: "hidden",
                                          transform: "scaleX(0)",
                                          transition: "all 0.3s ease-in-out 0s",
                                        },
                                        transition: "all 0.2s ease-in-out",
                                      },
                                      "&:hover span::before": { visibility: "visible", transform: "scaleX(1)" },
                                      "&:disabled, &.disabled": { color: "#d1d2db", pointerEvents: "none" },
                                      "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.95) !important", background: "#252525 !important", borderBottomColor: "rgba(255, 255, 255, 0.12) !important" },
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                    }}
                                    aria-expanded={
                                      openAccordion ===
                                      section.sectionName
                                    }
                                  >
                                    <span>
                                      {section.sectionName} Questions
                                    </span>
                                    <FontAwesomeIcon icon={['fas', 'angle-up']} sx={{ fontSize: "1.265rem !important" }} data-hf="accordion-icon" />
                                  </Button>
                                </Box>
                              </Box>
                            </Box>
                            <Collapse
                              in={
                                openAccordion === section.sectionName
                              }
                            >
                              <CardContent
                                style={{
                                  marginTop: -10,
                                  marginBottom: -10,
                                  marginLeft: 10,
                                  marginRight: 10}}
                              >
                                <Grid
                                  container
                                  spacing={2}
                                  sx={{ mt: 1 }}
                                >
                                  {section.fields}
                                </Grid>
                              </CardContent>
                            </Collapse>
                          </Card>
                        </Card>
                      ),
                    )}
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions
              style={{
                position: 'sticky',
                bottom: 0,
                backgroundColor: 'white',
                zIndex: 5000}}
            >
              <Button onClick={() => handleCloseAttempt()}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
              >
                {contact ? 'Update Contact' : 'Save Contact'}
              </Button>
            </DialogActions>
          </form>

          {/* Image Cropper Dialog */}
          {openEditImage &&
            (get(files, '0.url') || get(files, '0.preview')) && (
              <ImageCropper
                open={openEditImage}
                imageSrc={
                  get(files, '0.url') || get(files, '0.preview')
                }
                saveImage={saveCroppedImage}
                handleClose={handleCloseEditImage}
              />
            )}

          {/* Image Operations Menu */}
          <Menu
            anchorEl={editImageMoreVert}
            keepMounted
            open={Boolean(editImageMoreVert)}
            onClose={handleEditImageMoreVertClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'}}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'}}
          >
            <Box sx={{ p: "0.5rem" }}>
              <List component="div" sx={[{
                width: "100%",
                "& .MuiListItem-root, & .MuiListItemButton-root": { borderRadius: "0.65rem", width: "100%" },
                "& .MuiListItem-root, & .MuiListItemButton-root, .MuiList-root.nav-transparent-alt .MuiListItem-root, .MuiList-root.nav-transparent-alt .MuiListItemButton-root": { width: "100%" },
                "& .MuiListItem-root.MuiListItem-button, & .MuiListItemButton-root": { borderRadius: "0.65rem", width: "100%" },
                p: 0,
                m: 0,
                flexDirection: "column",
              }, {
                "& .MuiListItem-root.MuiListItem-button.Mui-selected, & .MuiListItem-root.MuiListItem-button:hover, & .MuiListItemButton-root.Mui-selected, & .MuiListItemButton-root:hover": {
                  color: "#4f5062",
                  background: "rgba(79, 80, 98, 0.05)",
                  "&::before": { background: "#4f5062" },
                },
              }]}>
                <ListItem onClick={handleEditImage}>
                  <EditIcon style={{ marginRight: 10 }} />
                  Edit Image
                </ListItem>
                <ListItem onClick={openDropzone}>
                  <Grid container direction="row" sx={{
                    alignItems: "center"
                  }}>
                    <Grid>
                      <FontAwesomeIcon icon={['fas', 'upload']} style={{ marginRight: 10 }} sx={{ fontSize: "1.15rem !important" }} />
                    </Grid>
                    <Grid>
                      <Typography>Upload New Image</Typography>
                    </Grid>
                  </Grid>
                </ListItem>
              </List>
            </Box>
          </Menu>
        </BlockUi>
      </Dialog>

      {/* Confirmation Dialog for Unsaved Changes */}
      <Dialog
        open={showConfirmClose}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes. Are you sure you want to leave
            and lose your changes?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmedClose}
            color="error"
            variant="contained"
          >
            Leave Without Saving
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AddContactDialog
